import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import DefaultButton from "../../../../components/buttons/defaultButton";
import { 
  getPayrollRunDetail,
  exportPayslips,
  exportBankFile,
  exportStatutoryReports,
  retryPayrollRun,
  approvePayrollRun,
  rejectPayrollRun,
} from "../../../../../services/workspace/payrollManagement.service";
import { 
  FiDownload, 
  FiRefreshCw, 
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiArrowLeft
} from "react-icons/fi";
import toast from "react-hot-toast";
import { downloadFile } from "../../../../../utils/helper";

const items = [{ label: "Payroll Run Detail", path: "" }];

const PayrollRunDetail = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { limit } = useSelector((state) => state.global);
  const [loading, setLoading] = useState(true);
  const [runDetail, setRunDetail] = useState(null);
  const [entries, setEntries] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [entriesCurrentPage, setEntriesCurrentPage] = useState(1);

  useEffect(() => {
    fetchRunDetail();
  }, [runId]);

  useEffect(() => {
    setEntriesCurrentPage(1);
  }, [entries.length]);

  const fetchRunDetail = async () => {
    try {
      setLoading(true);
      const runDetail = await getPayrollRunDetail(runId);
      
      if (runDetail) {
        setRunDetail(runDetail);
        setEntries(runDetail.entries || []);
        setExceptions(runDetail.exceptions || []);
        setActivityLog(runDetail.activity_log || []);
      } else {
        // Fallback mock data for development
        const mockDetail = {
          id: runId,
        period: "Nov 2025",
        period_start: "2025-11-01",
        period_end: "2025-11-30",
        run_type: "FINAL",
        status: "COMPLETED",
        initiated_by: "John Doe",
        created_at: "2025-11-25T10:30:00Z",
        finalized_at: "2025-11-25T11:15:00Z",
        finalized_by: "John Doe",
        snapshot_id: "snapshot_001",
        run_hash: "abc123def456",
        summary: {
          total_employees: 156,
          processed: 156,
          exceptions: 3,
          total_gross: 2450000,
          total_net: 1985000,
          total_deductions: 465000,
        },
        entries: [
          {
            id: "entry_001",
            employee_id: "emp_001",
            employee_name: "John Doe",
            employee_code: "EMP001",
            gross_salary: 50000,
            deductions: 8500,
            net_salary: 41500,
            components: {
              basic: 20000,
              hra: 10000,
              special_allowance: 15000,
              transport: 2500,
              medical: 2500,
            },
            contributions: {
              pf: 2400,
              esi: 375,
              pt: 200,
              tax: 5525,
            },
          },
          {
            id: "entry_002",
            employee_id: "emp_002",
            employee_name: "Jane Smith",
            employee_code: "EMP002",
            gross_salary: 75000,
            deductions: 12750,
            net_salary: 62250,
            components: {
              basic: 30000,
              hra: 15000,
              special_allowance: 22500,
              transport: 3750,
              medical: 3750,
            },
            contributions: {
              pf: 3600,
              esi: 562.5,
              pt: 200,
              tax: 8387.5,
            },
          },
        ],
        exceptions: [
          {
            id: "exc_001",
            type: "MISSING_BANK",
            employee_id: "emp_001",
            employee_name: "John Doe",
            message: "Bank account details missing",
            severity: "HIGH",
            resolved: false,
          },
        ],
        activity_log: [
          { timestamp: "2025-11-25T10:30:00Z", action: "Run started", user: "John Doe" },
          { timestamp: "2025-11-25T10:35:00Z", action: "Processing employees...", user: "System" },
          { timestamp: "2025-11-25T10:40:00Z", action: "Run completed", user: "System" },
          { timestamp: "2025-11-25T11:15:00Z", action: "Run finalized", user: "John Doe" },
        ],
        };
        setRunDetail(mockDetail);
        setEntries(mockDetail.entries);
        setExceptions(mockDetail.exceptions);
        setActivityLog(mockDetail.activity_log);
      }
    } catch (error) {
      console.error("Run detail fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch payroll run details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "DRAFT").toUpperCase();
    const statusConfig = {
      DRAFT: { color: "bg-gray-100 text-gray-700", label: "Draft" },
      PROCESSING: { color: "bg-blue-100 text-blue-700", label: "Processing" },
      DRY_COMPLETED: { color: "bg-yellow-100 text-yellow-700", label: "Dry Run Completed" },
      READY_FOR_FINALIZE: { color: "bg-purple-100 text-purple-700", label: "Ready to Finalize" },
      APPROVED: { color: "bg-green-100 text-green-700", label: "Approved" },
      FINALIZING: { color: "bg-indigo-100 text-indigo-700", label: "Finalizing" },
      COMPLETED: { color: "bg-success-100 text-success-700", label: "Completed" },
      FAILED: { color: "bg-error-100 text-error-700", label: "Failed" },
      ROLLED_BACK: { color: "bg-orange-100 text-orange-700", label: "Rolled Back" },
    };
    const config = statusConfig[normalizedStatus] || statusConfig.DRAFT;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleExport = async (type) => {
    try {
      toast.loading(`Exporting ${type}...`);
      let blob;
      let filename;
      
      if (type === "Payslips") {
        blob = await exportPayslips(runId);
        // filename = `payslips_${runId}.zip`;
        downloadFile(blob.data.download_url,blob.data.file_name)
        toast.dismiss();
        toast.success(`${type} exported successfully!`);
      } else if (type === "Bank CSV") {
        blob = await exportBankFile(runId, "hdfc"); // Default to HDFC format
        filename = `bank_file_${runId}.csv`;
      } else if (type === "Statutory Reports") {
        blob = await exportStatutoryReports(runId, "PF,ESI,PT,24Q");
        filename = `statutory_reports_${runId}.zip`;
      }
      
      // if (blob) {
      //   // Create download link
      //   const url = window.URL.createObjectURL(blob);
      //   const link = document.createElement("a");
      //   link.href = url;
      //   link.download = filename;
      //   document.body.appendChild(link);
      //   link.click();
      //   document.body.removeChild(link);
      //   window.URL.revokeObjectURL(url);
        
      //   toast.dismiss();
      //   toast.success(`${type} exported successfully!`);
      // }
    } catch (error) {
      console.error("Export error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || `Failed to export ${type}`);
    }
  };

  const handleRetry = async () => {
    try {
      toast.loading("Retrying payroll run...");
      await retryPayrollRun(runId);
      toast.dismiss();
      toast.success("Payroll run retried successfully!");
      fetchRunDetail();
    } catch (error) {
      console.error("Retry error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to retry payroll run");
    }
  };

  const handleApprove = async () => {
    try {
      toast.loading("Approving payroll run...");
      await approvePayrollRun(runId, { comment: "Approved for finalization" });
      toast.dismiss();
      toast.success("Payroll run approved successfully!");
      fetchRunDetail();
    } catch (error) {
      console.error("Approve error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to approve payroll run");
    }
  };

  const handleReject = async () => {
    const reason = prompt("Please enter rejection reason:");
    if (!reason || !reason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    try {
      toast.loading("Rejecting payroll run...");
      await rejectPayrollRun(runId, { reason });
      toast.dismiss();
      toast.success("Payroll run rejected");
      fetchRunDetail();
    } catch (error) {
      console.error("Reject error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to reject payroll run");
    }
  };

  const entriesTableData = entries.map((entry) => ({
    id: entry?.id || entry?.uuid || entry?.employee_id || entry?.employee_code,
    employee_code: {
      data: entry.employee_code,
      variant: "text",
    },
    employee_name: {
      data: entry.employee_name,
      variant: "text",
    },
    gross_salary: {
      data: formatCurrency(entry.gross_salary),
      variant: "text",
    },
    deductions: {
      data: formatCurrency(entry.deductions),
      variant: "text",
    },
    net_salary: {
      data: formatCurrency(entry.net_salary),
      variant: "text",
    },
  }));

  const paginatedEntriesTableData = entriesTableData.slice(
    (entriesCurrentPage - 1) * limit,
    entriesCurrentPage * limit
  );

  const entriesTableHeaders = [
    { label: "Employee Code", key: "employee_code" },
    { label: "Employee Name", key: "employee_name" },
    { label: "Gross Salary", key: "gross_salary" },
    { label: "Deductions", key: "deductions" },
    { label: "Net Salary", key: "net_salary" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-theme text-lg">Loading run details...</div>
      </div>
    );
  }

  if (!runDetail) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-error-600">Payroll run not found</div>
      </div>
    );
  }

  const currentStatus = String(
    runDetail?.status || runDetail?.run_status || "DRAFT"
  ).toUpperCase();

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        isSearch={false}
        filter={false}
        shorting={false}
        breadcrumbs={items}
      />

      {/* Header Actions */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-borderColor">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/hr/payroll-dashboard")}
            className="text-theme hover:text-themeHover"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-theme">{runDetail.period} Payroll Run</h2>
            <p className="text-sm text-textLight">Run ID: {runDetail.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(currentStatus)}
          {currentStatus === "FAILED" && (
            <DefaultButton
              name="Retry"
              icon={<FiRefreshCw className="w-4 h-4" />}
              onClick={handleRetry}
            />
          )}
          {(currentStatus === "DRY_COMPLETED" || currentStatus === "PENDING_APPROVAL" || currentStatus === "READY_FOR_FINALIZE") && (
            <>
              <DefaultButton
                name="Approve"
                variant="success"
                icon={<FiCheckCircle className="w-4 h-4" />}
                onClick={handleApprove}
              />
              <DefaultButton
                name="Reject"
                variant="danger"
                icon={<FiXCircle className="w-4 h-4" />}
                onClick={handleReject}
              />
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard label="Total Employees" value={runDetail.summary.total_employees} />
        <SummaryCard label="Processed" value={runDetail.summary.processed} color="text-success-600" />
        <SummaryCard label="Exceptions" value={runDetail.summary.exceptions} color="text-error-600" />
        <SummaryCard label="Total Gross" value={formatCurrency(runDetail.summary.total_gross)} />
        <SummaryCard label="Total Net" value={formatCurrency(runDetail.summary.total_net)} color="text-success-600" />
      </div>

      {/* Run Info */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Run Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoItem
            label="Period"
            value={`${formatDateOnly(runDetail.period_start)} to ${formatDateOnly(
              runDetail.period_end
            )}`}
          />
          <InfoItem label="Run Type" value={runDetail.run_type} />
          <InfoItem label="Initiated By" value={runDetail.initiated_by} />
          <InfoItem label="Created At" value={formatDate(runDetail.created_at)} />
          {runDetail.finalized_at && (
            <>
              <InfoItem label="Finalized By" value={runDetail.finalized_by} />
              <InfoItem label="Finalized At" value={formatDate(runDetail.finalized_at)} />
            </>
          )}
        </div>
      </div>

      {/* Export Actions */}
      {currentStatus === "COMPLETED" && (
        <div className="bg-white p-4 rounded-lg border border-borderColor">
          <h3 className="text-lg font-semibold text-theme mb-4">Export Files</h3>
          <div className="flex flex-wrap gap-3">
            <DefaultButton
              name="Export Payslips (ZIP)"
              icon={<FiDownload className="w-4 h-4" />}
              onClick={() => handleExport("Payslips")}
            />
            
          </div>
        </div>
      )}

      {/* Payroll Entries */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Payroll Entries</h3>
        <CommonTable
          TableHeaderData={entriesTableHeaders}
          tabledata={paginatedEntriesTableData}
          currentPage={entriesCurrentPage}
          totalData={entries.length}
          setCurrentPage={setEntriesCurrentPage}
          loading={false}
          action={{}}
        />
      </div>

      {/* Exceptions */}
      {exceptions.length > 0 && (
        <div className="bg-white p-4 rounded-lg border border-borderColor">
          <h3 className="text-lg font-semibold text-theme mb-4">Exceptions</h3>
          <div className="space-y-2">
            {exceptions.map((exception) => (
              <div
                key={exception.id}
                className="p-3 bg-error-50 rounded-lg border border-error-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-error-700">{exception.employee_name}</p>
                    <p className="text-sm text-error-600">{exception.message}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    exception.severity === "HIGH" 
                      ? "bg-error-200 text-error-800"
                      : "bg-warning-200 text-warning-800"
                  }`}>
                    {exception.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Activity Log</h3>
        <div className="space-y-2">
          {activityLog.map((log, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-theme mt-2"></div>
              <div className="flex-1">
                <p className="text-sm text-theme">{log.action}</p>
                <p className="text-xs text-textLight">
                  {formatDate(log.timestamp)} by {log.user}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color = "text-theme" }) => {
  return (
    <div className="bg-white p-4 rounded-lg border border-borderColor">
      <p className="text-sm text-textLight mb-1">{label}</p>
      <p className={`text-xl font-semibold ${color}`}>{value}</p>
    </div>
  );
};

const InfoItem = ({ label, value }) => {
  return (
    <div>
      <p className="text-xs text-textLight mb-1">{label}</p>
      <p className="text-sm font-medium text-theme">{value}</p>
    </div>
  );
};

export default PayrollRunDetail;

